import axios, { AxiosInstance, AxiosRequestConfig, CancelTokenSource, CancelTokenStatic } from 'axios'
import { Dictionary, URL_MAPPING } from '@zunta/configurations'
import { User } from '@/classes/User'
import WillowChatManager from '@/utils/WillowChatManager'
import { bus } from '@/plugins/eventBus'
import { DownloadParams } from './types/download'
import axiosRetry from 'axios-retry'

const jwtDecode = require('jwt-decode')
const CancelToken: CancelTokenStatic = axios.CancelToken
const { auth } = URL_MAPPING

interface Error {
  error: any;
  status: 'failed';
}

interface HttpError {
  message: any;
  response: any;
}

type RequestOptions = {
  isCompressed?: boolean,
}

export default class Http {
  protected instance: AxiosInstance
  private cancelTokens: { [k: string]: CancelTokenSource } = {}
  private token: string = ''
  public user: User | null = null
  private refreshTime: number = 5 * 60 * 1000
  private isLocalToken: boolean = false

  constructor () {
    const host = process.env.VUE_APP_SERVER_URL
    this.instance = axios.create({
      baseURL: `${host}/api/`,
      headers: {
        'Content-Type': 'application/json'
      },
      validateStatus: function (status) {
        return status >= 200 && status < 300
      }
    })
    const localStorageKey: string = 'front-version'

    axiosRetry(this.instance, {
      retries: 2,
      retryDelay: () => {
          return 2000
      },
      retryCondition: (error) => {
        if (error && error.response && error.response.data.message) {
          return error.response.status === 503 && error.response.data.message.includes('maintenance')
        }
        return false
      }
    })

    this.instance.interceptors.response.use((response: any): any => {
      const currentVersion = response.headers[localStorageKey]
      const storedVersion = localStorage.getItem(localStorageKey)
      if (!storedVersion || storedVersion === 'undefined') {
        localStorage.setItem(localStorageKey, currentVersion)
      } else if (currentVersion !== storedVersion) {
        localStorage.setItem(localStorageKey, currentVersion)
        console.log('New app version. Reloading Page....')
        // window.alert('New app version is available. We`ll reload the app...')
        window.location.reload()
      }

      return response.data
    }, (error: HttpError): Error | null => {
      if (error.response && error.response.status === 401 &&
        window.location.pathname !== '/login' && window.location.pathname !== '/resident/sign-up/password'
      ) {
        window.location.href = '/login?nextUrl=' + window.location.pathname
      }

      let message = 'Server error'
      if (error.response && error.response.status === 503 && (error.response.data.message && error.response.data.message.includes('maintenance'))) {
        message = 'Our servers are currently undergoing maintenance. Please try again in 2 minutes.'
      } else if (error.response && error.response.status >= 500 && (window.location.pathname === '/login' || window.location.pathname === '/resident/enter')) {
        message = `Something went wrong on the server. Error code: ${error.response.status}`
      }

      if (error.response.status >= 500) {
        bus.$emit('notify', {
          color: 'var(--status-error-color)',
          header: 'Error',
          message,
          icon: 'warning'
        })
      }

      if (error.response && error.response.status === 404 && window.location.pathname === '/app/admin/uploads') {
        bus.$emit('notify', {
          color: 'var(--status-error-color)',
          header: 'Error',
          message: `File not found`,
          icon: 'warning'
        })
        return null
      }

      return error.response && error.response.data
    })
  }

  setToken (token: string, isLocal: boolean = true) {
    try {
      const tokenData = jwtDecode(token)
      this.user = new User(tokenData)
      if (this.user.isExpired()) {
        this.user = null
        return
      }
      this.token = token
      this.isLocalToken = isLocal
      if (isLocal) {
        localStorage.setItem('jwt', token)
      } else {
        sessionStorage.setItem('jwt', token)
      }
      this.instance.defaults.headers.common['Authorization'] = `Bearer ${token}`
      bus.$emit('initToken', this.token)
      // @ts-ignore
      // eslint-disable-next-line no-inner-declarations
      function setCookie (name: any, value: any, days = 7) {
        let expires = ''
        if (days) {
          let date = new Date()
          date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
          expires = ''
          expires = '' + date.toUTCString()
        }
        document.cookie = name + '=' + (value || '') + ';' + expires + '; path=/'
      }

      setCookie('viewed-updates-video', JSON.stringify(this.user.getViewedUpdatesVideos() || []))
    } catch (e) {
      console.log(e)
    }
  }

  validateToken () {
    return !!this.token && !!this.user && !this.user.isExpired()
  }

  getToken () {
    return this.token
  }

  async requestWrapper (func: Function): Promise<any> {
    if (this.user && this.user.isExpired()) {
      console.error('user', this.user)
      this.logout(true)
      if (window.location.pathname.indexOf('login') !== -1) {
        return { statusCode: 401 }
      }
    }
    await this.updateTokenIfNeed()
    // eslint-disable-next-line no-return-await
    return await func()
  }

  async updateTokenIfNeed () {
    if (this.user) {
      if (this.user.getTimeUntilExpire() < this.refreshTime) {
        await this.refreshToken()
      }
    }
  }

  async refreshToken () {
    const response = await this.instance.post(`${auth.prefix}${auth.refreshToken.path}`) as any
    if (!response) {
      return
    }
    const { accessToken } = response
    if (accessToken) {
      this.setToken(accessToken, this.isLocalToken)
    }
  }

  async logout (silent: boolean = false) {
    if (silent) {
      await this.instance.post(`${auth.prefix}${auth.logout.path}`)
    }
    this.token = ''
    this.instance.defaults.headers.common['Authorization'] = ''
    WillowChatManager.logout()
    sessionStorage.removeItem('jwt')
    localStorage.removeItem('jwt')
    this.user = null
  }

  doGet (
    url: string,
    params: Dictionary<any> = {},
    key: string | null = null,
    requestOptions: RequestOptions = {}
  ): Promise<any> {
    return this.requestWrapper(() => {
      const requestId = key || url
      if (this.cancelTokens[requestId]) {
        this.cancelTokens[requestId].cancel('Canceled to prevent race condition')
      }
      const source = CancelToken.source()
      this.cancelTokens[requestId] = source
      const config: AxiosRequestConfig = requestOptions.isCompressed
        ? { params, cancelToken: source.token, headers: { 'x-compress': true } }
        : { params, cancelToken: source.token }

      return this.instance.get(url, config)
        .then((response: any) => {
          delete this.cancelTokens[requestId]
          return response
        })
        .catch((thrown: any) => {
          delete this.cancelTokens[requestId]
          if (axios.isCancel(thrown)) {
          } else {
            return thrown
          }
        })
    })
  }

  doPost (url: string, data: Object = {}, config: Object = {}): Promise<any> {
    return this.requestWrapper(() => {
      return this.instance.post(url, data, config)
    })
  }

  doPut (url: string, data: Object = {}): Promise<any> {
    return this.requestWrapper(() => {
      return this.instance.put(url, data)
    })
  }

  doPatch (url: string, data: Object = {}, config: Object = {}): Promise<any> {
    return this.requestWrapper(() => {
      return this.instance.patch(url, data, config)
    })
  }

  doDelete (url: string): Promise<any> {
    return this.requestWrapper(() => {
      return this.instance.delete(url)
    })
  }

 async doFetch<T> (url: string, data?: object, method: string = 'GET', getResponse?: boolean): Promise<T | boolean | Response> {
    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      }
    }

    let theUrl = this.instance.defaults.baseURL + url
    if (method !== 'GET') {
      options.body = JSON.stringify(data)
    } else if (data) {
      const urlParams = new URLSearchParams({ ...data })
      theUrl += `?${urlParams.toString()}`
    }

    try {
      const response = await fetch(theUrl, options)
      if (getResponse) return response
      if (!response.ok) {
        return false
      }
      const jsonResponse = await response.json() as T
      return jsonResponse
    } catch (error) {
      console.error('Fetch error:', error)
      return false
    }
  }

  doGetCompressedDownload (urlStr: string, params: Object = {}, fileName: string = '') {
    const baseUrl = this.instance.defaults.baseURL
    const token = this.token
    // @ts-ignore
    const urlParams = new URLSearchParams({ ...params, token })
    return this.requestWrapper(async () => {
      const response = await this.instance.get(`${baseUrl}${urlStr}?${urlParams.toString()}`, {
        headers: {
          'x-compress': true,
          ...this.instance.defaults.headers
        },
        responseType: 'arraybuffer'
      }) as ArrayBuffer

      if (!response) { return }

      const link = document.createElement('a')
      link.href = URL.createObjectURL(new Blob([response]))
      document.body.appendChild(link)

      link.setAttribute('download', fileName)
      link.setAttribute('target', '_blank')
      link.click()
      link.remove()
      URL.revokeObjectURL(link.href)
      return 'OK'
    })
  }

  doGetDownload (urlStr: string, params: Object = {}, fileName: string = '') {
    const baseUrl = this.instance.defaults.baseURL
    const token = this.token
    // @ts-ignore
    const urlParams = new URLSearchParams({ ...params, token })
    const link = document.createElement('a')
    link.href = `${baseUrl}${urlStr}?${urlParams.toString()}`
    document.body.appendChild(link)
    link.setAttribute('download', fileName)
    link.setAttribute('target', '_blank')
    link.click()
    link.remove()
  }

  async downloadFilesFromUrl (downloadParams: DownloadParams): Promise<{
    message: string;
    hasCorruptFiles: boolean;
    status: number
  }> {
    let responseObj: { message: string, error: string, status: number } | null = null
    let url: string = ''
    let fileName: string = ''
    const baseUrl = this.instance.defaults.baseURL
    const token = this.token
    if (downloadParams.statement) {
      const { urlStr, filtersDTO, statementReferenceId } = downloadParams.statement
      url = `${baseUrl}${urlStr}?filtersDTO=${JSON.stringify(filtersDTO)}&token=${token}&type=${JSON.stringify(statementReferenceId.type)}`
      fileName = `${statementReferenceId.type}_${statementReferenceId.id}.zip`
    } else if (downloadParams.pccUpload) {
      const { urlStr, ids, fileName: pccUploadFileName } = downloadParams.pccUpload
      fileName = pccUploadFileName
      url = `${baseUrl}${urlStr}/${ids}/${fileName}?token=${token}`
    }
    const response = await fetch(url)
    if (response.ok) {
      const link = document.createElement('a')
      document.body.appendChild(link)
      link.setAttribute('href', url)
      link.setAttribute('download', `${fileName}.zip`)
      link.click()
      link.remove()
    } else {
      responseObj = await response.json()
    }
    const message = (responseObj && responseObj.message) || response.statusText
    const error = (responseObj && !!responseObj.error) || response.statusText !== 'OK'
    return {
      message: message,
      hasCorruptFiles: error,
      status: response.status
    }
  }
}
